import React from 'react';
import Icon from '../../../components/AppIcon';

const ModuleProgressSummary = ({ module, progressData }) => {
  const calculateProgress = (completed, quantity) => {
    if (!quantity || quantity === 0) return 0;
    return Math.round((completed / quantity) * 100);
  };

  const progress = calculateProgress(module?.completed, module?.quantity);

  const milestones = [
    { label: 'In Pipeline', value: progressData?.inPipeline || 0, icon: 'Inbox', color: 'var(--color-warning)' },
    { label: 'Started', value: progressData?.started || 0, icon: 'Play', color: 'var(--color-primary)' },
    { label: 'On Floor', value: progressData?.onFloor || 0, icon: 'Activity', color: 'var(--color-success)' },
    { label: 'Completed', value: progressData?.completed || 0, icon: 'CheckCircle', color: 'var(--color-success)' }
  ];

  const getProgressColor = (progress) => {
    if (progress >= 90) return 'var(--color-success)';
    if (progress >= 50) return 'var(--color-primary)';
    if (progress >= 25) return 'var(--color-warning)';
    return 'var(--color-error)';
  };

  return (
    <div className="bg-card rounded-xl border border-border p-5 h-full">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="TrendingUp" size={16} color="var(--color-primary)" />
        </div>
        <h3 className="text-base font-semibold text-foreground">Progress Summary</h3>
      </div>

      {/* Overall Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">Overall Completion</span>
          <span className="text-2xl font-bold text-foreground">{progress}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${progress}%`,
              backgroundColor: getProgressColor(progress)
            }}
          />
        </div>
      </div>

      {/* Module Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="text-center p-2 bg-muted/30 rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">Total</p>
          <p className="text-lg font-bold text-foreground">{module?.quantity || 0}</p>
        </div>
        <div className="text-center p-2 bg-success/10 rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">Completed</p>
          <p className="text-lg font-bold text-success">{module?.completed || 0}</p>
        </div>
        <div className="text-center p-2 bg-warning/10 rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">Remaining</p>
          <p className="text-lg font-bold text-warning">{(module?.quantity || 0) - (module?.completed || 0)}</p>
        </div>
      </div>

      {/* Milestone Tracking */}
      <div className="border-t border-border pt-3">
        <p className="text-xs font-medium text-muted-foreground mb-2">Production Milestones</p>
        <div className="space-y-2">
          {milestones?.map((milestone, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name={milestone?.icon} size={14} color={milestone?.color} />
                <span className="text-xs text-foreground">{milestone?.label}</span>
              </div>
              <span className="text-xs font-semibold text-foreground">{milestone?.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Delay Warning */}
      {module?.status === 'delayed' && progressData?.delayReasons && (
        <div className="mt-3 p-2 bg-error/10 rounded-lg border border-error/20">
          <div className="flex items-start gap-2">
            <Icon name="AlertTriangle" size={14} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-error mb-0.5">Delay Detected</p>
              <p className="text-xs text-muted-foreground">{progressData?.delayReasons}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModuleProgressSummary;